import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { verifyEmailCode } from "../behavior/api/auth";
import { PageRoute, PageState } from "../constants";
import TransitionPage from "../pages/transition/TransitionPage";

const EmailVerification = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const shouldExecuteRef = useRef(true);
    const navigate = useNavigate();

    const verifyCode = useCallback(
        async (code: string, email: string): Promise<void> => {
            try {
                const response = await verifyEmailCode(code, email);

                if (!response) {
                    toast.error(t("AppMessages.server-error"));
                    return;
                }
                const encodedEmail = encodeURIComponent(email);
                if (response.isError && response.errorMessage) {
                    toast.error(t(`AppMessages.${response.errorMessage}`));
                    if (response.errorMessage === PageState.EMAIL_PREVIOUSLY_VERIFIED) {
                        navigate(`/?state=${PageState.EMAIL_PREVIOUSLY_VERIFIED}&email=${encodedEmail}`, {
                            replace: true,
                        });
                        return;
                    }
                    navigate(PageRoute.ROOT, { replace: true });
                } else {
                    navigate(`/?state=${PageState.VERIFIED_EMAIL}&email=${encodedEmail}`, {
                        replace: true,
                    });
                }
            } catch (e) {
                toast.error(t((e as Error).message));
            }
        },
        [navigate, t],
    );

    useEffect(() => {
        if (!shouldExecuteRef.current) {
            return;
        }
        shouldExecuteRef.current = false;

        const mode = searchParams.get("mode");
        const code = searchParams.get("oobCode");
        const email = searchParams.get("email");

        if (mode === "verifyEmail" && code && email) {
            verifyCode(code, email);
        } else {
            navigate(PageRoute.ROOT, { replace: true });
        }
    }, [navigate, verifyCode, searchParams]);

    return (
        <TransitionPage
            title={t("AppMessages.verifying-email-title")}
            subtitle={t("AppMessages.verifying-email-subtitle")}
        />
    );
};

export default EmailVerification;
